import { useCallback, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ApiResponseBackupRuns } from '../types/api/ApiResponseBackup';
import type { BackupRun, BackupRunsParent } from '../types/models/Backup';

/** One loaded page of a subject's restore points, plus the parent echo the endpoint returns. */
export interface BackupRunsPage {
  runs: BackupRun[];
  /** Total runs for this subject ignoring paging — equals its `backup_count`. */
  count: number;
  limit: number;
  offset: number;
  parent: BackupRunsParent | null;
}

export interface LoadRunsOptions {
  limit?: number;
  offset?: number;
}

const DEFAULT_LIMIT = 200;

/**
 * `GET /datacenters/backups/{id}/runs` — restore points, `run_at` DESC.
 *
 * **Imperative on purpose**: nothing is fetched on mount. Two callers drive it:
 *
 * - the tree table, which lazily loads a subject's children the first time it is expanded (and
 *   keeps the cached page when it is re-collapsed);
 * - the detail view's paginated runs table, which re-loads the same subject as the page changes.
 *
 * State is keyed by subject id so one hook instance can serve many rows. Out-of-order responses
 * are dropped via a per-subject request sequence, which matters for the paginated caller: clicking
 * through pages quickly must not let a slow earlier response overwrite a newer one.
 *
 * Remember this list mirrors the disk — a pruned restore point simply disappears. It is not a
 * history: never diff two loads, and never read a shrinking `count` as data loss.
 */
export function useBackupRuns() {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [runsById, setRunsById] = useState<Record<number, BackupRunsPage | undefined>>({});
  const [loadingById, setLoadingById] = useState<Record<number, boolean | undefined>>({});
  const [errorById, setErrorById] = useState<Record<number, Error | undefined>>({});
  const requestSeqRef = useRef<Record<number, number>>({});

  const loadRuns = useCallback(
    async (backupId: number, options: LoadRunsOptions = {}) => {
      if (!auth.user?.access_token) return;

      const limit = options.limit ?? DEFAULT_LIMIT;
      const offset = options.offset ?? 0;

      const seq = (requestSeqRef.current[backupId] ?? 0) + 1;
      requestSeqRef.current[backupId] = seq;
      const isCurrent = () => requestSeqRef.current[backupId] === seq;

      setLoadingById((prev) => ({ ...prev, [backupId]: true }));
      setErrorById((prev) => ({ ...prev, [backupId]: undefined }));

      try {
        const response = await apiFetch(`/datacenters/backups/${backupId}/runs?limit=${limit}&offset=${offset}`);
        const json = (await response.json().catch(() => null)) as ApiResponseBackupRuns | null;

        if (json === null) {
          throw new Error(`Unexpected response from server (HTTP ${response.status})`);
        }
        if (json.status !== 'success' || !json.data) {
          throw new Error(json.message ?? 'Unexpected response from server');
        }

        if (!isCurrent()) return;

        const data = json.data;
        setRunsById((prev) => ({
          ...prev,
          [backupId]: {
            runs: data.runs ?? [],
            count: data.count ?? 0,
            limit: data.limit ?? limit,
            offset: data.offset ?? offset,
            parent: data.backup ?? null,
          },
        }));
      } catch (err) {
        if (!isCurrent()) return;
        setErrorById((prev) => ({
          ...prev,
          [backupId]: err instanceof Error ? err : new Error('An unexpected error occurred'),
        }));
      } finally {
        if (isCurrent()) {
          setLoadingById((prev) => ({ ...prev, [backupId]: false }));
        }
      }
    },
    [auth.user?.access_token, apiFetch],
  );

  return { runsById, loadingById, errorById, loadRuns };
}
